import { useEffect, useRef, useState } from "react";
import { ChatCircleDots, PaperPlaneTilt, X, Sparkle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const SUGGESTIONS = ["Bu ay en çok kim borçlandı?", "Vadesi geçen alacaklarım ne durumda?", "Hangi ürünler kritik stokta?", "Bu ay ne kadar tahsilat yaptım?"];
const sidKey = "bc-ai-chat-sid";

export default function AiChat() {
  const [open, setOpen] = useState(false);
  const [sid] = useState(() => localStorage.getItem(sidKey) || (() => { const s = `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`; localStorage.setItem(sidKey, s); return s; })());
  const [msgs, setMsgs] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const endRef = useRef(null);

  useEffect(() => { if (open) api.get(`/ai/chat/${sid}`).then((r) => setMsgs(r.data.messages)).catch(() => {}); }, [open, sid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const ask = async (text) => {
    const message = (text ?? q).trim();
    if (!message || busy) return;
    setQ("");
    setErr("");
    setMsgs((m) => [...m, { role: "user", content: message }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { session_id: sid, message }, { timeout: 90000 });
      setMsgs(data.messages);
    } catch (e) {
      setErr(errMsg(e));
      setMsgs((m) => m.slice(0, -1));
    }
    setBusy(false);
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="no-print fixed bottom-5 right-5 z-40 flex items-center gap-2 rounded-full bg-zinc-950 px-4 py-3 text-sm font-bold text-white shadow-xl transition-transform hover:-translate-y-0.5" data-testid="ai-chat-open-button">
        <Sparkle weight="fill" className="text-[#fdba74]" /> Asistan
      </button>
      {open && (
        <div className="no-print fixed bottom-5 right-5 z-50 flex h-[560px] w-[380px] max-w-[calc(100vw-2rem)] flex-col border border-zinc-800 bg-zinc-950 text-white shadow-2xl" data-testid="ai-chat-panel">
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
            <div className="flex items-center gap-2 font-display font-bold"><ChatCircleDots weight="fill" className="text-[#2dd4bf]" /> Veri Asistanı</div>
            <button type="button" onClick={() => setOpen(false)} className="text-zinc-400 hover:text-white" aria-label="Kapat" data-testid="ai-chat-close-button"><X /></button>
          </div>
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3 text-sm" data-testid="ai-chat-messages">
            {msgs.length === 0 && (
              <div className="space-y-2">
                <p className="text-xs text-zinc-400">İşletme verilerinize bakarak yanıtlar. Örnekler:</p>
                {SUGGESTIONS.map((s) => <button key={s} type="button" onClick={() => ask(s)} className="block w-full border border-white/10 bg-white/5 px-3 py-2 text-left text-xs hover:bg-white/10" data-testid="ai-chat-suggestion">{s}</button>)}
                <p className="pt-2 text-xs text-zinc-400" data-testid="ai-chat-actions-hint">Eylem de verebilirsiniz: <i>"[Cari adı]'na hatırlatma gönder"</i> veya <i>"... mutabakat gönder"</i> — e-posta anında gönderilir.</p>
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`max-w-[90%] whitespace-pre-wrap px-3 py-2 ${m.role === "user" ? "ml-auto bg-[#2dd4bf] text-zinc-950" : "bg-white/10"}`} data-testid={`ai-chat-msg-${m.role}`}>{m.content}</div>
            ))}
            {busy && <div className="w-fit bg-white/10 px-3 py-2 text-xs text-zinc-300" data-testid="ai-chat-thinking">Veriler inceleniyor…</div>}
            {err && <div className="text-xs text-red-400" data-testid="ai-chat-error">{err}</div>}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2 border-t border-white/10 p-3">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Sorunuzu yazın…" className="flex-1 bg-white/10 px-3 py-2 text-sm outline-none placeholder:text-zinc-500" data-testid="ai-chat-input" />
            <Button type="submit" disabled={busy || !q.trim()} className="rounded-[4px] bg-[#fdba74] text-zinc-950 hover:bg-[#fb923c]" data-testid="ai-chat-send-button"><PaperPlaneTilt weight="fill" /></Button>
          </form>
        </div>
      )}
    </>
  );
}
